import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useOcelMetadata } from "../../../stores/OcelContext";
import { useActivityTypes } from "../ActivityReoccurrenceKPI/hooks/useActivityTypes";
import { useEventAttributes } from "./hooks/useEventAttributes";
import { useObjectDateAttributes } from "./hooks/useObjectDateAttributes";
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import DateDeviationKPISettings from './DateDeviationKPISettings';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';
import { useAutoFitFontSize } from '../_shared/useAutoFitFontSize';

const DATE_DEVIATION_VALUE_MAX_PX = 60; // was text-6xl
const DATE_DEVIATION_VALUE_MIN_PX = 20;

// Extracted so useAutoFitFontSize is called from a real component's render, not from a nested
// helper function like renderKPICard() — hooks called from a non-component helper violate the
// Rules of Hooks. No compact/abbreviated fallback: this is a day-count deviation value, not a
// large count, so it doesn't make sense abbreviated.
const DateDeviationBigValue = ({ days }) => {
  const text = days !== null && days !== undefined ? days.toFixed(2) : '—';
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: DATE_DEVIATION_VALUE_MAX_PX,
    minPx: DATE_DEVIATION_VALUE_MIN_PX,
  });

  return (
    <div
      ref={ref}
      className="max-w-full truncate font-bold mb-2 text-primary-500"
      style={{ fontSize: `${fontSizePx}px` }}
    >
      {text}
    </div>
  );
};

const DateDeviationKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  defaultActivity,
  defaultDateAttribute1,
  defaultDateAttribute2,
  defaultCondition,
  defaultAggregation,
  defaultKpiTitle,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  // ✅ Use local filters hook - automatically selects local or global filters
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const isInitialMount = useRef(true);
  const containerRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < 260);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Form state
  const [mode, setMode] = useState('event'); // 'event' | 'object'
  const [activity, setActivity] = useState('');
  const [objectType, setObjectType] = useState('');
  const [dateAttribute1, setDateAttribute1] = useState('');
  const [dateAttribute2, setDateAttribute2] = useState('');
  const [condition, setCondition] = useState('greater');
  const [aggregation, setAggregation] = useState('median');
  const [kpiTitle, setKpiTitle] = useState('Date Deviation KPI');

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Custom hooks for dropdown data
  const metadata = useOcelMetadata(activeFile);
  const objectTypes = useMemo(
    () => (metadata?.object_types ? Object.keys(metadata.object_types) : []),
    [metadata]
  );
  const { activityTypes, loading: activityTypesLoading} = useActivityTypes(activeFile);
  const { eventAttributes, loading: eventAttributesLoading } = useEventAttributes(activeFile, activity);
  const { dateAttributes: objectDateAttributes, loading: objectDateAttributesLoading } = useObjectDateAttributes(activeFile, objectType);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.mode !== undefined) setMode(initialSettings.mode);
    if (initialSettings.activity !== undefined) setActivity(initialSettings.activity);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.dateAttribute1 !== undefined) setDateAttribute1(initialSettings.dateAttribute1);
    if (initialSettings.dateAttribute2 !== undefined) setDateAttribute2(initialSettings.dateAttribute2);
    if (initialSettings.condition !== undefined) setCondition(initialSettings.condition);
    if (initialSettings.aggregation !== undefined) setAggregation(initialSettings.aggregation);
    if (initialSettings.kpiTitle !== undefined) setKpiTitle(initialSettings.kpiTitle);
    // Mark as configured so auto-load-after-defaults triggers a fetch once
    // all the restored state values land in the next render cycle.
    setHasFetched(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ mode, activity, objectType, dateAttribute1, dateAttribute2, condition, aggregation, kpiTitle, localFilters }));
  }, [mode, activity, objectType, dateAttribute1, dateAttribute2, condition, aggregation, kpiTitle, localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved kpiTitle.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(kpiTitle || "Date Deviation KPI");
  }, [kpiTitle]);

  // Mode toggle: reset attribute selections that depend on the previous mode's source
  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    setDateAttribute1('');
    setDateAttribute2('');
  }, []);

  // Object type change: reset date attributes (depend on the selected object type)
  const handleObjectTypeChange = useCallback((newObjectType) => {
    setObjectType(newObjectType);
    setDateAttribute1('');
    setDateAttribute2('');
  }, []);

  const handleOpenSettings = useCallback((e) => {
    e.stopPropagation(); // Prevent grid drag activation
    startDraftMode(); // Enter draft mode when opening modal
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);


  // Reset date attributes when activity changes (cascade behavior) - Event mode only
  // ✅ Modified to only auto-select if attributes are empty (preserves defaults and user selections)
  useEffect(() => {
    if (mode !== 'event') return;

    if (activity && eventAttributes.length > 0) {
      // Only auto-populate if both attributes are empty (allows defaults to persist)
      if (!dateAttribute1 && !dateAttribute2) {
        // Auto-select first two attributes if available
        if (eventAttributes.length >= 2) {
          // For activities with multiple date attributes, use the first two
          setDateAttribute1(eventAttributes[0]);
          setDateAttribute2(eventAttributes[1]);
          console.log(`✅ Auto-selected attributes: ${eventAttributes[0]}, ${eventAttributes[1]}`);
        } else if (eventAttributes.length === 1) {
          // If only one attribute available, select it for the first dropdown
          setDateAttribute1(eventAttributes[0]);
          setDateAttribute2('');
          console.log(`✅ Auto-selected first attribute: ${eventAttributes[0]}`);
        }
      }
    } else if (!activity && !isInitialMount.current) {
      // Clear date attributes when no activity is selected
      setDateAttribute1('');
      setDateAttribute2('');
    }
  }, [mode, activity, eventAttributes, dateAttribute1, dateAttribute2]);

  // Reset date attributes when object type changes (cascade behavior) - Object mode only
  useEffect(() => {
    if (mode !== 'object') return;

    if (objectType && objectDateAttributes.length > 0) {
      if (!dateAttribute1 && !dateAttribute2) {
        if (objectDateAttributes.length >= 2) {
          setDateAttribute1(objectDateAttributes[0]);
          setDateAttribute2(objectDateAttributes[1]);
          console.log(`✅ Auto-selected object date attributes: ${objectDateAttributes[0]}, ${objectDateAttributes[1]}`);
        } else if (objectDateAttributes.length === 1) {
          setDateAttribute1(objectDateAttributes[0]);
          setDateAttribute2('');
          console.log(`✅ Auto-selected first object date attribute: ${objectDateAttributes[0]}`);
        }
      }
    } else if (!objectType && !isInitialMount.current) {
      setDateAttribute1('');
      setDateAttribute2('');
    }
  }, [mode, objectType, objectDateAttributes, dateAttribute1, dateAttribute2]);

  // ✅ One-time initialization with default values (runs only on mount)
  useEffect(() => {
    let hasChanges = false;

    // Apply default title if provided and still at default value
    if (defaultKpiTitle && kpiTitle === 'Date Deviation KPI') {
      setKpiTitle(defaultKpiTitle);
      if (onTitleChange) {
        onTitleChange(defaultKpiTitle);
      }
      hasChanges = true;
      console.log(`✅ Applied default KPI title: "${defaultKpiTitle}"`);
    }

    // Apply default activity if provided and still empty
    if (defaultActivity && activity === '') {
      setActivity(defaultActivity);
      hasChanges = true;
      console.log(`✅ Applied default activity: "${defaultActivity}"`);
    }

    // Apply default date attributes if provided and still empty
    if (defaultDateAttribute1 && dateAttribute1 === '') {
      setDateAttribute1(defaultDateAttribute1);
      hasChanges = true;
      console.log(`✅ Applied default date attribute 1: "${defaultDateAttribute1}"`);
    }

    if (defaultDateAttribute2 && dateAttribute2 === '') {
      setDateAttribute2(defaultDateAttribute2);
      hasChanges = true;
      console.log(`✅ Applied default date attribute 2: "${defaultDateAttribute2}"`);
    }

    // Apply default condition if provided and still at default
    if (defaultCondition && condition === 'greater') {
      setCondition(defaultCondition);
      hasChanges = true;
      console.log(`✅ Applied default condition: "${defaultCondition}"`);
    }

    // Apply default aggregation if provided and still at default
    if (defaultAggregation && aggregation === 'median') {
      setAggregation(defaultAggregation);
      hasChanges = true;
      console.log(`✅ Applied default aggregation: "${defaultAggregation}"`);
    }

    // If we applied defaults, mark as configured and trigger auto-load
    if (hasChanges) {
      console.log('✅ DateDeviationKPI initialized with defaults, will auto-load');
      setHasFetched(true); // Mark as configured to enable auto-load
    }

    // Mark initialization as complete
    isInitialMount.current = false;
  }, []); // Empty deps = run only once on mount

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchKPIData = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    // Validate form before making request
    const referenceSelected = mode === 'event' ? activity : objectType;
    if (!referenceSelected || !dateAttribute1 || !dateAttribute2 || !kpiTitle) {
      // Don't set error on initial load - let user configure
      if (hasFetched) {
        setError("Please fill in all required fields");
      }
      return;
    }

    if (dateAttribute1 === dateAttribute2) {
      setError("Date attributes must be different");
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("🔄 Fetching date deviation KPI data...", {
        activeFile,
        mode,
        activity,
        objectType,
        dateAttribute1,
        dateAttribute2,
        condition,
        aggregation,
        kpiTitle
      });

      // ✅ Filters are automatically merged by api (local or global)
      const response = await api.post('/date-deviation-kpi', {
        mode,
        ...(mode === 'event' ? { activity } : { object_type: objectType }),
        date_attribute_1: dateAttribute1,
        date_attribute_2: dateAttribute2,
        condition,
        aggregation,
        kpi_title: kpiTitle,
      }, {}, 'Date Deviation KPI');

      console.log("📥 Date Deviation KPI response received:", response);
      if (seq !== fetchSeqRef.current) return;
      setData(response);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("❌ Error during KPI data fetch:", err);
      setError(err.message || "An unknown error occurred.");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [activeFile, mode, activity, objectType, dateAttribute1, dateAttribute2, condition, aggregation, kpiTitle, api, hasLocalFilters, currentFilters]);

  // ✅ Auto-reload when filters change (local or global)
  useEffect(() => {
    // Skip auto-reload during initial mount (defaults haven't applied yet)
    if (isInitialMount.current) {
      return;
    }

    if (hasFetched && !loading) {
      fetchKPIData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  // Auto-load after defaults are applied
  useEffect(() => {
    if (!isInitialMount.current && hasFetched && !loading && !data) {
      fetchKPIData();
    }
  }, [hasFetched, loading, data, fetchKPIData]);

  const hasData = !loading && !error && data !== null;
  const showInitialButton = !hasFetched && !loading;

  // Handle apply settings - defined after fetchKPIData
  // pendingApply triggers a fetch via useEffect AFTER state updates land,
  // so fetchKPIData always runs with the latest settings/filters.
  const handleApplySettings = useCallback(() => {
    applyDraftFilters();
    setPendingApply(true);

    if (onTitleChange && kpiTitle) {
      onTitleChange(kpiTitle);
    }

    setIsSettingsModalOpen(false);
  }, [applyDraftFilters, onTitleChange, kpiTitle]);

  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchKPIData();
  }, [pendingApply, fetchKPIData]);

  // Header actions - settings button and local filters badge
  const headerActions = useMemo(() => (
    <div className="flex items-center gap-2">
      {hasLocalFilters && (
        <LocalFiltersActiveBadge
          hasLocalFilters={hasLocalFilters}
          localFilterCount={localFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
      )}
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters]);

  const isFormValid = () => {
    if (!dateAttribute1 || !dateAttribute2 || !kpiTitle || dateAttribute1 === dateAttribute2) {
      return false;
    }

    if (mode === 'object') {
      return !!objectType &&
             !objectDateAttributesLoading &&
             objectDateAttributes.length > 0;
    }

    return !!activity &&
           !activityTypesLoading &&
           !eventAttributesLoading &&
           eventAttributes.length > 0;
  };

  // Handle Enter key press on form inputs - update title
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Update the tile title
      if (onTitleChange && kpiTitle) {
        onTitleChange(kpiTitle);
      }
    }
  }, [kpiTitle, onTitleChange]);

  // Disabled auto-load - user must click "Calculate KPI" button
  // useAutoLoad(fetchKPIData, [activeFile, api.currentFilters, activity, dateAttribute1, dateAttribute2, condition, aggregation, kpiTitle], hasData || !isFormValid());


  // Provide refresh function to parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchKPIData);
    }
  }, [onRefresh, fetchKPIData]);

  // Provide header actions to parent (GridComponent)
  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  const renderKPICard = () => {
    if (!data) return null;

    const aggregationLabel = {
      'median': 'Median',
      'average': 'Average',
      'minimum': 'Minimum',
      'maximum': 'Maximum'
    }[data.aggregation] || data.aggregation;

    const conditionLabel = {
      'greater': '>',
      'less': '<',
      'equal': '='
    }[data.condition] || data.condition;

    const isObjectMode = data.mode === 'object';
    const referenceLabel = isObjectMode ? 'Object Type' : 'Activity';
    const referenceValue = isObjectMode ? data.object_type : data.activity;
    const countLabel = isObjectMode ? 'Objects' : 'Events';

    return (
      <div ref={containerRef} className="flex flex-col gap-4 w-full">
        {/* KPI Title - Display custom title */}
        {data.kpi_title && data.kpi_title !== 'Date Deviation KPI' && (
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800">{data.kpi_title}</h3>
          </div>
        )}

        <div className="rounded-md p-4 flex-1 flex flex-col items-center justify-center text-center min-h-[120px]">
          <DateDeviationBigValue days={data.result_days} />
          <div className="text-lg font-medium text-gray-700 mb-1">
            {aggregationLabel} Deviation (days)
          </div>
          <div className="text-sm text-gray-500 text-center max-w-md">
            {data.date_attribute_1} {conditionLabel} {data.date_attribute_2}
          </div>
          {referenceValue && (
            <div className="text-xs text-gray-400 text-center mt-1">
              {referenceLabel}: {referenceValue}
            </div>
          )}
        </div>

        <div className={`grid ${isNarrow ? 'grid-cols-1' : 'grid-cols-2'} gap-4 text-center`}>
          <div className="bg-gray-50 rounded-md p-3">
            <div className="text-2xl font-bold text-gray-700">
              {data.events_meeting_condition}
            </div>
            <div className="text-xs text-gray-500">{countLabel} Meeting Condition</div>
          </div>
          <div className="bg-gray-50 rounded-md p-3">
            <div className="text-2xl font-bold text-gray-700">
              {data.total_events}
            </div>
            <div className="text-xs text-gray-500">Total {countLabel}</div>
          </div>
        </div>
      </div>
    );
  };

  console.log('🎨 DateDeviationKPI render - showInitialButton:', showInitialButton);

  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={handleOpenSettings}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* Settings Panel */}

        {/* KPI Results */}
        {hasData && (
          <div className="pt-2">
            {renderKPICard()}
          </div>
        )}

        {/* No Data Message */}
        {hasFetched && !loading && !error && !hasData && (
          <TileStatusContent
            status="empty"
            title="No Data Found"
            message="No events were found matching the specified criteria. Try adjusting your filters or date attributes."
            action={
              <button
                onClick={fetchKPIData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}

        {/* Error State with Retry */}
        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchKPIData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Date Deviation KPI Settings"
        isApplyDisabled={!activeFile || !isFormValid() || loading}
        applyButtonText={loading ? "Calculating..." : hasFetched ? "Update KPI" : "Calculate KPI"}
      >
        <DateDeviationKPISettings
          kpiTitle={kpiTitle}
          onKpiTitleChange={setKpiTitle}
          mode={mode}
          onModeChange={handleModeChange}
          activity={activity}
          onActivityChange={setActivity}
          objectType={objectType}
          onObjectTypeChange={handleObjectTypeChange}
          dateAttribute1={dateAttribute1}
          onDateAttribute1Change={setDateAttribute1}
          dateAttribute2={dateAttribute2}
          onDateAttribute2Change={setDateAttribute2}
          condition={condition}
          onConditionChange={setCondition}
          aggregation={aggregation}
          onAggregationChange={setAggregation}
          activityTypes={activityTypes}
          eventAttributes={eventAttributes}
          objectTypes={objectTypes}
          objectDateAttributes={objectDateAttributes}
          loading={loading}
          activityTypesLoading={activityTypesLoading}
          eventAttributesLoading={eventAttributesLoading}
          objectDateAttributesLoading={objectDateAttributesLoading}
          onKeyPress={handleKeyPress}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default DateDeviationKPI;
